/**
 * Every `type` string NotificationHelper.AddNotificationAsync is ever called
 * with, backend-wide. It has to be every one of them: NotificationBell maps
 * this union to a title and a body, so a type the backend sends but this
 * union omits renders as a notification with no text at all -- which is
 * exactly what happened to the whole second half of this list until now.
 *
 * Grepping the backend for AddNotificationAsync is NOT enough to find them
 * all: three call sites (CheckInOutController, BookingDisputeController's
 * NotifyAsync) pass the type in a VARIABLE, and those nine types were
 * missed exactly that way the first time. NotificationBell also now falls
 * back to a generic title/body rather than rendering nothing, so the next
 * type someone forgets degrades instead of disappearing.
 * Adding a backend notification type means adding it here too; the
 * Record<HeavyDutyMachineryNotificationType, string> title map then forces
 * the rest.
 */
export type HeavyDutyMachineryNotificationType =
  | 'NewBooking'
  | 'NewMessage'
  | 'BookingStatusChanged'
  | 'AuditPassed'
  | 'AuditFailed'
  | 'AdminAnnouncement'
  | 'PayoutsDisabled'
  | 'PayoutsRestored'
  | 'BookingConfirmed'
  | 'BookingPaymentFailed'
  | 'SpaceApproved'
  | 'SpaceRejected'
  | 'AdminMessage'
  | 'GeofenceAlert'
  | 'GeofenceResolved'
  | 'GeofenceZoneSet'
  | 'GeofenceMoveRequested'
  | 'GeofenceMoveApproved'
  | 'GeofenceMoveRejected'
  | 'OvertimeChargeFailed'
  // Reserva diferida (BookingController's deferred branch / AcceptQuote /
  // ProposeQuoteAmount).
  | 'NewQuoteRequest'
  | 'QuoteAccepted'
  | 'QuoteRevised'
  // Booking lifecycle milestones (BookingController's transport-started /
  // off-rent / collection-started actions).
  | 'TransportStarted'
  | 'OffRentDeclared'
  | 'CollectionStarted'
  // Money (BookingFundsReleaseService, BookingLineTransferService).
  | 'FundsReleased'
  | 'TransferPendingResolution'
  // Part of a booking's money was handed back without the booking ending
  // (BookingRefundHelper) -- sent to the guest AND to every payee whose own
  // lines just shrank, since on a multi-provider booking that is separate
  // news for each of them.
  | 'BookingRefunded'
  // El depósito del host (HostDepositService) -- dinero suyo, así que se le
  // cuenta tanto cuando entra como cuando se le descuenta.
  | 'HostDepositFunded'
  | 'HostDepositCharged'
  // Cobro a la entrega (BookingPaymentAuthorizationService) -- el cliente es
  // el único que puede arreglar una tarjeta que no aguanta la retención, y
  // tiene hasta 48h antes de la entrega para hacerlo.
  | 'BookingFundsHeld'
  | 'BookingAuthorizationFailed'
  | 'BookingCharged'
  // Petición de presupuesto a un servicio (ServiceRequestsController,
  // BookingController's own conversion, ServiceRequestExpiryService). A
  // request is not a booking -- see ServiceRequest's doc comment -- so it has
  // its own family rather than reusing the deferred-booking quote types above.
  | 'NewServiceRequest'
  | 'ServiceRequestQuoted'
  | 'ServiceRequestAccepted'
  | 'ServiceRequestDeclined'
  | 'ServiceRequestCancelled'
  | 'ServiceRequestExpired'
  | 'ServiceVisitScheduled'
  // Accepted and then left hanging: the customer said yes and the
  // professional never said when. One type per side, because "they never
  // fixed a date" and "you never fixed a date" are not the same sentence.
  | 'ServiceVisitNotConfirmed'
  | 'ServiceVisitConfirmationMissed'
  // The professional declaring the work done (BookingController.
  // MarkServiceCompleted) -- the service side's albarán. It goes to the
  // customer, because it starts their own 24 hours to say something before the
  // money is released.
  | 'ServiceCompleted'
  // Disputes (DisputeDeadlineService).
  | 'DisputeClosedByDeadline'
  // Operator verification (AdminOperatorsController).
  | 'OperatorSuspended'
  | 'OperatorReinstated'
  | 'ServiceProviderSuspended'
  | 'ServiceProviderReinstated'
  // Albarán -- the counterparty's verdict on a check-in/out report
  // (CheckInOutController, which picks the type from the acknowledgement
  // outcome rather than passing a literal).
  | 'HandoverAcknowledged'
  | 'HandoverRejected'
  // The deposit hold could not be placed before the machine goes out
  // (DepositAuthorizationService) -- only the guest can fix it, and only
  // while there is still time.
  | 'DepositAuthorizationFailed'
  // Claims (BookingDisputeController, all sent through its own NotifyAsync
  // with the type as a parameter).
  | 'DisputeOpened'
  | 'DisputeAnswered'
  | 'DisputeOffer'
  | 'DisputeSettled'
  | 'DisputeSplit'
  | 'DisputeWithdrawn'
  | 'DisputeResolved';

/**
 * The notifications that mean "a booking you may be looking at just changed",
 * so any screen showing bookings can refetch on them.
 *
 * Stated once, here beside the union it is drawn from, because the bug it
 * fixes is one of omission and every screen would otherwise grow its own
 * partial copy. IncomingBookings listened for 'NewBooking' alone -- which a
 * reserva diferida never sends, since it arrives as 'NewQuoteRequest' -- so
 * the host's list silently went stale. The bell rang, the host clicked it,
 * and the landing effect found no such booking in a list that had never
 * reloaded, so the click went nowhere.
 *
 * Deliberately broad: refetching one list is cheap, and the cost of a missing
 * entry here is a screen that quietly lies about the state of somebody's
 * money. Anything that moves a booking's status, price, milestones or dispute
 * belongs in it. NewMessage does not -- the thread has its own live channel
 * (onReceiveMessage) and appending a message is not a change to the booking.
 */
export const BOOKING_CHANGED_NOTIFICATION_TYPES: HeavyDutyMachineryNotificationType[] = [
  'NewBooking',
  'BookingStatusChanged',
  'BookingConfirmed',
  'BookingPaymentFailed',
  'NewQuoteRequest',
  'QuoteAccepted',
  'QuoteRevised',
  // The one service-request type that creates a booking. The rest move a
  // request, which no booking list shows.
  'ServiceVisitScheduled',
  'TransportStarted',
  'OffRentDeclared',
  'CollectionStarted',
  'BookingRefunded',
  'HandoverAcknowledged',
  'HandoverRejected',
  'DepositAuthorizationFailed',
  'FundsReleased',
  'DisputeOpened',
  'DisputeAnswered',
  'DisputeOffer',
  'DisputeSettled',
  'DisputeSplit',
  'DisputeWithdrawn',
  'DisputeResolved',
  'DisputeClosedByDeadline',
];

export interface IHeavyDutyMachineryNotification {
  id: string;
  userId: string;
  type: HeavyDutyMachineryNotificationType;
  actorName?: string;
  contentTitle?: string;
  link?: string;
  body?: string;

  /** The reference of the booking this is about, e.g. "R-PPTAR". Absent on anything that isn't about one, and on every notification raised before this existed. */
  bookingReference?: string;
  createdAt: string;
  read: boolean;
}

/** One cursor-paginated page from GET .../notifications -- same shape as IConversationsPage. */
export interface IHeavyDutyMachineryNotificationsPage {
  items: IHeavyDutyMachineryNotification[];
  hasMore: boolean;
}
